import { Data } from '@things-factory/shell'
import { Arg, Ctx, Directive, Mutation, Resolver } from 'type-graphql'
import { In } from 'typeorm'

import { Board } from '../board/board.js'
import { PlayGroup } from './play-group.js'
import { NewPlayGroup, PlayGroupPatch } from './play-group-type.js'

@Resolver(PlayGroup)
export class PlayGroupMutation {
  @Mutation(returns => PlayGroup, { description: 'Creates a new play group.' })
  @Directive('@transaction')
  async createPlayGroup(
    @Arg('playGroup') playGroup: NewPlayGroup,
    @Ctx() context: ResolverContext
  ): Promise<PlayGroup> {
    const { domain, user, tx } = context.state

    return await tx.getRepository(PlayGroup).save({
      domain,
      ...playGroup,
      creator: user,
      updater: user
    })
  }

  @Mutation(returns => PlayGroup, { description: 'Updates an existing play group.' })
  @Directive('@transaction')
  async updatePlayGroup(
    @Arg('id') id: string,
    @Arg('patch') patch: PlayGroupPatch,
    @Ctx() context: ResolverContext
  ): Promise<PlayGroup> {
    const { domain, user, tx } = context.state
    const repository = tx.getRepository(PlayGroup)

    const playGroup = await repository.findOneBy({ domain: { id: domain.id }, id })

    return await repository.save({
      ...playGroup,
      ...patch,
      updater: user
    })
  }

  @Mutation(returns => PlayGroup, { description: 'Sets the custom playback order for boards in a play group.' })
  @Directive('@transaction')
  async reorderPlayGroup(
    @Arg('id') id: string,
    @Arg('boardIds', type => [String]) boardIds: string[],
    @Ctx() context: ResolverContext
  ): Promise<PlayGroup> {
    const { domain, user, tx } = context.state
    const repository = tx.getRepository(PlayGroup)

    const playGroup = await repository.findOneBy({ domain: { id: domain.id }, id })

    return await repository.save({
      ...playGroup,
      order: boardIds,
      updater: user
    })
  }

  @Mutation(returns => PlayGroup, { description: 'Adds one or more boards to a play group.' })
  @Directive('@transaction')
  async joinPlayGroup(
    @Arg('id') id: string,
    @Arg('boardIds', type => [String]) boardIds: string[],
    @Ctx() context: ResolverContext
  ) {
    const { tx } = context.state

    const repository = tx.getRepository(PlayGroup)
    const playGroup = await repository.findOne({
      where: { id },
      relations: ['boards']
    })
    const order = playGroup.order || []

    const boardIdList = playGroup.boards.map(board => board.id)
    boardIds.forEach(boardId => {
      if (boardIdList.indexOf(boardId) == -1) {
        boardIdList.push(boardId)
      }
      if (order.indexOf(boardId) == -1) {
        order.push(boardId)
      }
    })

    const boardRepository = tx.getRepository(Board)
    playGroup.boards = await boardRepository.findBy({ id: In(boardIdList) })
    playGroup.updatedAt = new Date()
    playGroup.order = order

    return await repository.save(playGroup)
  }

  @Mutation(returns => PlayGroup, { description: 'Removes one or more boards from a play group.' })
  @Directive('@transaction')
  async leavePlayGroup(
    @Arg('id') id: string,
    @Arg('boardIds', type => [String]) boardIds: string[],
    @Ctx() context: ResolverContext
  ) {
    const { tx } = context.state

    const repository = tx.getRepository(PlayGroup)
    const playGroup = await repository.findOne({
      where: { id },
      relations: ['boards']
    })
    const order = playGroup.order || []

    const boardIdList = playGroup.boards.map(board => board.id)
    boardIds.forEach(boardId => {
      let index = boardIdList.indexOf(boardId)
      if (index !== -1) {
        boardIdList.splice(index, 1)
      }

      index = order.indexOf(boardId)
      if (index !== -1) {
        order.splice(index, 1)
      }
    })

    const boardRepository = tx.getRepository(Board)
    playGroup.boards = await boardRepository.findBy({ id: In(boardIdList) })
    playGroup.updatedAt = new Date()
    playGroup.order = order

    return await repository.save(playGroup)
  }

  @Mutation(returns => Boolean, { description: 'Deletes a play group.' })
  @Directive('@transaction')
  async deletePlayGroup(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state
    const repository = tx.getRepository(PlayGroup)

    /* TODO - 플레이 그룹은 소속된 보드-플레이그룹 관계들도 제거되어야 한다. */
    const playGroup = await repository.findOneBy({ domain: { id: domain.id }, id })

    await repository.delete(id)
    return true
  }
}
